import { Token } from '../text/types';
import { tokenSimilarity } from '../text/similarity';
import {
  AlignerConfig,
  AlignerMode,
  AlignerResult,
  CandidateHypothesis,
  EvidenceItem,
  MovementKind,
} from './types';

export const DEFAULT_ALIGNER_CONFIG: AlignerConfig = {
  backBandTracking: 15,
  forwardBandTracking: 60,
  backBandUncertain: 30,
  forwardBandUncertain: 120,
  largeJumpThreshold: 20,
  weakCountToUncertain: 3,
  weakCountToLost: 7,
  silenceFreezeMs: 3500,
  maxProvisionalAdvance: 4,
};

export class AlignmentEngine {
  private tokens: readonly Token[];
  private config: AlignerConfig;

  private pos = 0.0;
  private mode: AlignerMode = 'TRACKING';
  private confidence = 0.0;
  private lastConfirmedPos = 0;
  private consecutiveWeak = 0;
  private pendingLargeJump: { targetPos: number; count: number } | null = null;
  private lastEvidenceTimeMs = 0;

  constructor(tokens: readonly Token[], config: Partial<AlignerConfig> = {}) {
    this.tokens = tokens;
    this.config = { ...DEFAULT_ALIGNER_CONFIG, ...config };
  }

  setTokens(tokens: readonly Token[]): void {
    this.tokens = tokens;
    this.pos = Math.min(this.pos, Math.max(0, tokens.length - 1));
    this.lastConfirmedPos = Math.min(this.lastConfirmedPos, Math.max(0, tokens.length - 1));
  }

  getState(): { pos: number; mode: AlignerMode; confidence: number; lastConfirmedPos: number } {
    return {
      pos: this.pos,
      mode: this.mode,
      confidence: this.confidence,
      lastConfirmedPos: this.lastConfirmedPos,
    };
  }

  /**
   * Re-anchors alignment at a specific token (e.g. after manual user repositioning)
   */
  reanchor(tokenIndex: number): void {
    const clamped = Math.max(0, Math.min(this.tokens.length - 1, tokenIndex));
    this.pos = clamped;
    this.lastConfirmedPos = clamped;
    this.mode = 'UNCERTAIN';
    this.confidence = 0.5;
    this.consecutiveWeak = 0;
    this.pendingLargeJump = null;
  }

  /**
   * Called on audio silence detection
   */
  onSilence(nowMs: number): AlignerResult {
    const pauseDuration = this.lastEvidenceTimeMs > 0 ? nowMs - this.lastEvidenceTimeMs : 0;

    if (pauseDuration > this.config.silenceFreezeMs) {
      // Long silence: freeze motion, decay confidence
      this.confidence = Math.max(0.1, this.confidence * 0.98);
      if (this.mode === 'TRACKING') {
        this.mode = 'UNCERTAIN';
      }
    } else {
      // Short hesitation: slowly decay confidence without changing state
      this.confidence = Math.max(0.2, this.confidence * 0.995);
    }

    return {
      pos: this.pos,
      anchorTokenIndex: Math.round(this.pos),
      confidence: this.confidence,
      mode: this.mode,
      movementKind: 'none',
      debug: {
        bandStart: Math.round(this.pos),
        bandEnd: Math.round(this.pos),
        topScore: 0,
        candidates: [],
        consecutiveWeak: this.consecutiveWeak,
      },
    };
  }

  /**
   * Core semi-global alignment matching observation tokens against script
   */
  processEvidence(evidence: EvidenceItem, nowMs: number): AlignerResult {
    this.lastEvidenceTimeMs = nowMs;
    const numTokens = this.tokens.length;

    if (numTokens === 0 || evidence.obsTokens.length === 0) {
      return this.onSilence(nowMs);
    }

    // 1. Determine search band based on mode
    let bandStart = 0;
    let bandEnd = numTokens;

    const currentIntPos = Math.round(this.pos);

    if (this.mode === 'TRACKING') {
      bandStart = Math.max(0, currentIntPos - this.config.backBandTracking);
      bandEnd = Math.min(numTokens, currentIntPos + this.config.forwardBandTracking);
    } else if (this.mode === 'UNCERTAIN') {
      bandStart = Math.max(0, currentIntPos - this.config.backBandUncertain);
      bandEnd = Math.min(numTokens, currentIntPos + this.config.forwardBandUncertain);
    } else {
      // LOST or RECOVERING: full script search
      bandStart = 0;
      bandEnd = numTokens;
    }

    const bandLen = bandEnd - bandStart;
    const obs = evidence.obsTokens;
    const obsLen = obs.length;

    // 2. Semi-global alignment DP matrix
    // Row i represents obs[0..i-1], Column j represents band[0..j-1]
    // dp[i][j]: best alignment score
    const dp = new Float32Array((obsLen + 1) * (bandLen + 1));
    const getDP = (i: number, j: number) => dp[i * (bandLen + 1) + j]!;
    const setDP = (i: number, j: number, val: number) => {
      dp[i * (bandLen + 1) + j] = val;
    };

    // Free start for script: row 0 is 0 everywhere
    for (let j = 0; j <= bandLen; j++) {
      setDP(0, j, 0.0);
    }

    // ASR insertions penalty for column 0
    for (let i = 1; i <= obsLen; i++) {
      setDP(i, 0, -0.4 * i);
    }

    // Fill DP
    for (let i = 1; i <= obsLen; i++) {
      const oTok = obs[i - 1]!;

      for (let j = 1; j <= bandLen; j++) {
        const sTok = this.tokens[bandStart + j - 1]!;
        const sim = tokenSimilarity(oTok, sTok.norm);

        // Score substitution/match
        let matchScore: number;
        if (sim >= 0.7) {
          matchScore = sim * sTok.weight;
        } else {
          matchScore = -0.5 * (1 - sim);
        }

        const scoreMatch = getDP(i - 1, j - 1) + matchScore;
        const scoreSkipScript = getDP(i, j - 1) - 0.4 * sTok.weight;
        const scoreInsertAsr = getDP(i - 1, j) - 0.4;

        setDP(i, j, Math.max(scoreMatch, scoreSkipScript, scoreInsertAsr));
      }
    }

    // 3. Extract candidate end positions from bottom row (i = obsLen)
    const candidates: CandidateHypothesis[] = [];
    let maxRowScore = -Infinity;

    for (let j = 1; j <= bandLen; j++) {
      const score = getDP(obsLen, j);
      if (score > maxRowScore) {
        maxRowScore = score;
      }

      // Check for local peak
      const prev = j > 1 ? getDP(obsLen, j - 1) : -Infinity;
      const next = j < bandLen ? getDP(obsLen, j + 1) : -Infinity;

      if (score >= prev && score >= next && score > 0.5) {
        // Traceback or count matches for this candidate
        const tokenIdx = bandStart + j - 1;
        const { matchedCount, contentMatchedCount, longestRun } = this.evaluateCandidate(
          obs,
          tokenIdx
        );

        candidates.push({
          endPos: tokenIdx,
          score,
          matchedCount,
          contentMatchedCount,
          longestRun,
        });
      }
    }

    // Sort candidates by score descending
    candidates.sort((a, b) => b.score - a.score);
    // Keep top-K distinct candidates
    const topCandidates = candidates.slice(0, 4);

    const topCandidate = topCandidates[0];

    // 4. Candidate Scoring & Movement Prior
    let movement: MovementKind = 'none';

    if (!topCandidate || topCandidate.contentMatchedCount < 2 || topCandidate.score < 1.2) {
      // Weak evidence
      this.consecutiveWeak++;
      this.confidence = Math.max(0.15, this.confidence * 0.85);

      if (this.consecutiveWeak >= this.config.weakCountToLost) {
        this.mode = 'LOST';
      } else if (this.consecutiveWeak >= this.config.weakCountToUncertain) {
        this.mode = 'UNCERTAIN';
      }

      return {
        pos: this.pos,
        anchorTokenIndex: Math.round(this.pos),
        confidence: this.confidence,
        mode: this.mode,
        movementKind: 'none',
        debug: {
          bandStart,
          bandEnd,
          topScore: topCandidate?.score ?? 0,
          candidates: topCandidates,
          consecutiveWeak: this.consecutiveWeak,
        },
      };
    }

    // Strong candidate observed
    const delta = topCandidate.endPos - this.pos;

    // Movement evaluation & Acceptance Rules (§11.6)
    if (delta > this.config.largeJumpThreshold) {
      // Large forward relocation: requires confirmation across consecutive items or high-confidence FINAL
      if (
        (this.pendingLargeJump && Math.abs(this.pendingLargeJump.targetPos - topCandidate.endPos) <= 3) ||
        (evidence.kind === 'final' && topCandidate.score > 3.5 && topCandidate.contentMatchedCount >= 4)
      ) {
        // Confirmed relocation
        this.pos = topCandidate.endPos;
        this.lastConfirmedPos = topCandidate.endPos;
        movement = 'relocate-forward';
        this.mode = 'TRACKING';
        this.confidence = 0.85;
        this.consecutiveWeak = 0;
        this.pendingLargeJump = null;
      } else {
        // First observation of large jump: hold position, require confirmation
        this.pendingLargeJump = { targetPos: topCandidate.endPos, count: 1 };
        movement = 'none';
        this.mode = 'UNCERTAIN';
        this.confidence = 0.5;
      }
    } else if (delta < 0) {
      // Backward step (correction / repeated sentence)
      if (topCandidate.longestRun >= 3 && topCandidate.contentMatchedCount >= 2) {
        this.pos = topCandidate.endPos;
        this.lastConfirmedPos = topCandidate.endPos;
        movement = 'correct-back';
        this.confidence = 0.8;
        this.consecutiveWeak = 0;
        this.mode = 'TRACKING';
      } else {
        // Weak backward match: ignore to prevent jitter
        movement = 'none';
      }
    } else if (delta >= 0) {
      // Normal forward progress
      if (evidence.kind === 'provisional') {
        // Provisional update: advance conservatively capped
        const advance = Math.min(this.config.maxProvisionalAdvance, delta);
        this.pos = Math.max(this.lastConfirmedPos - 1, this.pos + advance);
      } else {
        // Final evidence: confirm position
        this.pos = topCandidate.endPos;
        this.lastConfirmedPos = topCandidate.endPos;
      }

      movement = 'advance';
      this.mode = 'TRACKING';
      this.confidence = Math.min(1.0, 0.6 + topCandidate.contentMatchedCount * 0.1);
      this.consecutiveWeak = 0;
      this.pendingLargeJump = null;
    }

    return {
      pos: this.pos,
      anchorTokenIndex: Math.round(this.pos),
      confidence: this.confidence,
      mode: this.mode,
      movementKind: movement,
      debug: {
        bandStart,
        bandEnd,
        topScore: topCandidate.score,
        candidates: topCandidates,
        consecutiveWeak: this.consecutiveWeak,
      },
    };
  }

  /**
   * Helper to count matched tokens and runs around candidate
   */
  private evaluateCandidate(
    obs: readonly string[],
    candidateEndIndex: number
  ): { matchedCount: number; contentMatchedCount: number; longestRun: number } {
    let matchedCount = 0;
    let contentMatchedCount = 0;
    let currentRun = 0;
    let longestRun = 0;

    const obsLen = obs.length;
    let sIdx = candidateEndIndex;

    for (let i = obsLen - 1; i >= 0 && sIdx >= 0; i--) {
      const oTok = obs[i]!;
      const sTok = this.tokens[sIdx];
      if (!sTok) break;

      const sim = tokenSimilarity(oTok, sTok.norm);
      if (sim >= 0.7) {
        matchedCount++;
        if (sTok.weight >= 0.4) {
          contentMatchedCount++;
        }
        currentRun++;
        if (currentRun > longestRun) {
          longestRun = currentRun;
        }
      } else {
        currentRun = 0;
      }
      sIdx--;
    }

    return { matchedCount, contentMatchedCount, longestRun };
  }
}
